import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Download, Refresh, CheckCircle } from '@mui/icons-material';
import apiService from '../services/apiService';

const ORDER_KIT_URL = 'https://www.paintmelike.co/products/custom-paint-by-number-kit';

const ResultsDisplay = ({ results, onDownload, onReset }) => {
  const [orderPopupOpen, setOrderPopupOpen] = useState(false);

  useEffect(() => {
    if (!results) return;

    const timer = setTimeout(() => {
      setOrderPopupOpen(true);
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, [results]);

  const handleClosePopup = () => {
    setOrderPopupOpen(false);
  };

  const handleOrderKit = () => {
    window.open(ORDER_KIT_URL, '_blank', 'noopener,noreferrer');
    setOrderPopupOpen(false);
  };

  if (!results) {
    return (
      <Alert severity="error">
        No results to display. Please try processing your image again.
      </Alert>
    );
  }

  const { file_id, output_files, settings_used } = results;

  const templateSrc = apiService.getFileUrl(file_id, 'template.png');

  const fileDescriptions = {
    "template.png": {
      title: 'Paint Template',
      description: 'The numbered template for painting',
      icon: '🎨',
    },
    "reference.png": {
      title: 'Color Reference',
      description: 'Color chart with numbered swatches',
      icon: '🎭',
    },
    "solution.png": {
      title: 'Solution Image',
      description: 'The final colored result',
      icon: '✨',
    },
  };

  return (
    <Box sx={{ p: 2 }}>
      <Dialog
        open={orderPopupOpen}
        onClose={handleClosePopup}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Love it?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Click below to order your kit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup} color="inherit">
            Maybe Later
          </Button>
          <Button onClick={handleOrderKit} variant="contained">
            Order Your Kit
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CheckCircle color="success" sx={{ fontSize: 32 }} />
        <Typography variant="h4">
          Your Paint by Numbers is Ready!
        </Typography>
      </Box>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body1">
          Successfully generated your paint-by-numbers template with {settings_used.num_colors} colors.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} >
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Download Your Files
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: 500,
                mx: "auto",
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "grey.50",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                component="img"
                src={templateSrc}
                alt="Paint by Numbers Template"
                sx={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "1 / 1",
                  objectFit: "contain",
                  borderRadius: 1,
                  display: "block",
                }}
              />
            </Box>

            <Grid container spacing={3}>
              {Object.entries(output_files).map(([fileType, fileName]) => {
                const fileInfo = fileDescriptions[fileType] || {
                  title: fileType.charAt(0).toUpperCase() + fileType.slice(1),
                  description: `Generated ${fileType} file`,
                  icon: '📄',
                };

                return (
                  <Grid item xs={12} sm={6} md={4} key={fileType}>
                    <Card
                      sx={{
                        height: 300,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        borderRadius: 4,
                        p: 1.5,
                        boxShadow: '0 6px 18px rgba(0,0,0,0.10)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        transition: 'all 0.25s ease',
                        backgroundColor: '#fff',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: '0 14px 30px rgba(0,0,0,0.16)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5, pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <Typography sx={{ fontSize: '2rem', lineHeight: 1 }}>
                            {fileInfo.icon}
                          </Typography>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 500,
                              fontSize: {
                                xs: '1.8rem',
                                sm: '2rem',
                              },
                              lineHeight: 1.1,
                              color: '#222',
                            }}
                          >
                            {fileInfo.title}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '1rem',
                            lineHeight: 1.5,
                            maxWidth: '90%',
                          }}
                        >
                          {fileInfo.description}
                        </Typography>
                      </CardContent>

                      <CardActions sx={{ p: 2.5, pt: 0 }}>
                        <Button
                          startIcon={<Download />}
                          onClick={() => onDownload(fileType)}
                          fullWidth
                          variant="contained"
                          size="large"
                          sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontSize: '1.05rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.28)',
                            '&:hover': {
                              boxShadow: '0 8px 18px rgba(25, 118, 210, 0.36)',
                            },
                          }}
                        >
                          Download
                        </Button>
                      </CardActions>
                    </Card>

                  </Grid>
                );
              })}
            </Grid>
            <Button
              startIcon={<Refresh />}
              onClick={onReset}
              variant="contained"
              fullWidth
              sx={{
                mt: 3,
                py: 2.2,
                borderRadius: '20px',
                backgroundColor: '#4CAF50',
                color: '#FFFFFF',
                fontSize: '1.6rem',
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                boxShadow: '0 6px 14px rgba(76, 175, 80, 0.35)',
                transition: 'all 0.25s ease',
                '&:hover': {
                  backgroundColor: '#43A047',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 10px 20px rgba(76, 175, 80, 0.45)',
                },
              }}
            >
              Create Another Image
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          File ID: {file_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Files will be available for download for 24 hours
        </Typography>
      </Box>
    </Box>
  );
};

export default ResultsDisplay;